import type { ElementType, ReactNode, FC, Ref } from "react";
import { isString, mergeClasses, rem } from "@huub-web/utils";

import { Flex, type FlexProps } from "../Flex";
import { Text, type TextProps } from "../Text";
import { Placeholder } from "../Placeholder";
import type { ContextAction } from "../../types";
import { ContextActionsPopup } from "../ContextActionsPopup";

import {
  descriptionStyle,
  headingStyle,
  titleContainerStyle,
  titleStyle,
} from "./Heading.css";
import { Ruler } from "../Ruler";

export type HeadingProps = Omit<FlexProps, "align" | "title"> & {
  /**
   * The subtitle of the heading.
   * This will be rendered as a text string.
   */
  subtitle?: string;
  /**
   * The value of the heading.
   */
  title?: ReactNode;
  /**
   * The element type of the title.
   */
  titleAs?: ElementType;
  /**
   * The description of the heading.
   * This will be rendered as a markdown string.
   */
  description?: ReactNode;
  /**
   * The variant of the heading.
   * This will determine the alignment of the heading and description.
   */
  variant?: "main" | "secondary" | "default" | "card";
  /**
   * The trailing content of the heading.
   */
  trailing?: ReactNode;
  /**
   * The leading content of the heading.
   */
  leading?: ReactNode;
  /**
   * Whether to render a ruler between the title and description.
   */
  ruler?: boolean;
  /**
   * Whether to render a loading state.
   */
  loading?: boolean;
  /**
   * The variant of the heading.
   * This will determine the alignment of the heading and description.
   */
  textVariant?: TextProps["variant"];
  /**
   * The alignment of the heading.
   */
  align?: "left" | "center";
  /**
   * The context actions of the heading.
   */
  contextActions?: ContextAction[];
  /**
   * The on context action of the heading.
   */
  onContextAction?: (action: ContextAction) => void;
  /**
   * The content after the heading.
   */
  childrenAfter?: ReactNode;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const Heading: FC<HeadingProps> = ({
  title,
  description,
  variant = "default",
  subtitle,
  titleAs: titleAsProp,
  trailing,
  leading,
  ruler = false,
  className,
  loading = false,
  textVariant: textVariantProp,
  align = "center",
  contextActions,
  onContextAction,
  children,
  childrenAfter,
  ref,
  ...props
}) => {
  const titleAs = (() => {
    if (titleAsProp) {
      return titleAsProp;
    }
    if (variant === "card") {
      return "h3";
    }
    return variant === "main" ? "h1" : "h2";
  })();

  return loading ? (
    <>
      <Placeholder height={rem(48)} />
      <Placeholder height={rem(24)} />
    </>
  ) : (
    <Flex
      ref={ref}
      direction="column"
      gap="m"
      className={mergeClasses(headingStyle, className)}
      align={align === "left" || !!trailing ? "flex-start" : align}
      {...props}
    >
      {leading}

      {subtitle && (
        <Text
          variant="surfaceSubtitle"
          color="currentColor50"
          align={align ? align : variant === "main" ? "center" : "left"}
        >
          {subtitle}
        </Text>
      )}

      {children}

      {(!!title ||
        !!trailing ||
        (contextActions && contextActions.length > 0)) && (
        <Flex
          gap="m"
          align="center"
          justify={align === "left" || !!trailing ? "space-between" : align}
          className={titleContainerStyle({
            variant,
            forceStretch:
              !!trailing || !!(contextActions && contextActions.length > 0),
          })}
        >
          <Text
            as={titleAs}
            align={align === "left" || !!trailing ? "left" : align}
            variant={textVariantProp}
            className={titleStyle({
              variant: textVariantProp ? undefined : variant,
            })}
          >
            {title}
          </Text>

          {trailing}

          {contextActions && contextActions.length > 0 && (
            <ContextActionsPopup
              actions={contextActions}
              title={title}
              onAction={onContextAction}
            />
          )}
        </Flex>
      )}

      {ruler && <Ruler variant="soft" />}

      {childrenAfter}

      {description && (
        <Text
          align={align === "left" || !!trailing ? "left" : align}
          markdown={isString(description)}
          className={descriptionStyle({
            variant,
          })}
        >
          {description}
        </Text>
      )}
    </Flex>
  );
};
